import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { Button, INK } from '../abf/onboarding/review/reviewStyles';
import { SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import { words } from '../abf/board/boardShared';
import { getPcAttention, pcErrorMessage } from './api';
import { PC_QUEUE_PATH } from './routes';
import type { PcAttentionItem } from './types';

/**
 * "What needs attention" on a private credit facility (PC workflow parity, slice 2): the same items the
 * work queue routes, worst first, with the server's role check, and the one action each one names.
 * Selecting an item shows it; the action opens the tab or record that owns it. Nothing here changes a
 * record.
 */

const Shell = styled.section`
  display: grid; grid-template-columns: minmax(16rem, 1fr) minmax(0, 2fr); gap: 14px; margin-bottom: 1rem;
  color: ${INK.body}; font-size: 14px; line-height: 1.5;
  h2, h3, p { margin: 0; } h2 { font-size: 15px; color: ${INK.strong}; } p { color: ${INK.muted}; font-size: 12.5px; }
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
const Rail = styled.div`
  border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.panel}; padding: 12px;
  .tasks { display: grid; gap: 6px; margin-top: 10px; max-height: 420px; overflow: auto; }
  .empty { padding: 10px 2px; color: ${INK.faint}; font-size: 13px; }
  .foot { margin-top: 10px; font-size: 12.5px; color: ${INK.muted}; a { color: ${INK.accentSoft}; } }
`;
const Item = styled.button<{ $active: boolean }>`
  text-align: left; width: 100%; appearance: none; cursor: pointer; display: grid; gap: 4px;
  padding: 9px 10px; border-radius: 9px; border: 1px solid ${p => p.$active ? '#43617f' : INK.line};
  background: ${p => p.$active ? INK.well : INK.raised}; color: ${INK.body}; font: inherit;
  &:hover { border-color: ${INK.lineStrong}; }
  .category { font-size: 11px; color: ${INK.faint}; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; }
  strong { font-size: 13px; line-height: 1.35; color: ${INK.strong}; }
  .meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 12px; color: ${INK.muted}; }
`;
const Work = styled.div`
  border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.panel}; padding: 14px 16px;
  .head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; }
  .meta { color: ${INK.muted}; font-size: 13px; margin-top: 8px; }
  .quiet { color: ${INK.faint}; font-size: 13px; }
`;

const severityTone = (item: PcAttentionItem): StatusTone =>
  item.triageState !== 'OPEN' ? 'neutral' : item.severity === 'CRITICAL' || item.sla === 'ESCALATED' ? 'critical'
    : item.severity === 'WARNING' || item.sla === 'OVERDUE' || item.sla === 'DUE_SOON' ? 'warning' : 'progress';

interface Props {
  loanId: number;
  /** Changes when the page re-read its records, so the rail re-reads too. */
  refreshKey?: number | string;
  onOpen: (workspace: string, query?: string | null) => void;
}

const FacilityAttentionRail: React.FC<Props> = ({ loanId, refreshKey, onOpen }) => {
  const [items, setItems] = useState<PcAttentionItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setItems(await getPcAttention(loanId)); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The attention items could not be read.')); }
    finally { setBusy(false); }
  }, [loanId]);
  useEffect(() => { void load(); }, [load, refreshKey]);
  const open = useMemo(() => (items || []).filter(i => i.triageState === 'OPEN'), [items]);
  const selected = useMemo(() => open.find(i => i.key === selectedKey) || open[0] || null, [open, selectedKey]);

  return <Shell aria-label="What needs attention">
    <Rail>
      <h2>What needs attention</h2>
      <p>{items === null ? "Reading the facility's records…" : open.length
        ? `${open.length} item${open.length === 1 ? '' : 's'} from exceptions, covenants, underwriting, amendments, tickets, the calendar and documents, worst first.`
        : 'Nothing open on this facility.'}</p>
      {error && <p role="alert" style={{ color: '#f8a5a5' }}>{error}</p>}
      <div className="tasks">
        {open.map(item => <Item key={item.key} type="button" $active={selected?.key === item.key} onClick={() => setSelectedKey(item.key)}>
          <span className="category">{words(item.workflow)} · {words(item.severity)}</span>
          <strong>{item.text}</strong>
          <span className="meta"><StatusChip tone={severityTone(item)} label={item.sla === 'UNASSIGNED' ? 'no due date' : words(item.sla)} />{item.dueDate ? `due ${item.dueDate}` : ''}{item.owner ? ` · ${item.owner}` : ''}</span>
        </Item>)}
        {items !== null && open.length === 0 && <div className="empty">No exception, covenant, decision, ticket, date or document is waiting.</div>}
      </div>
      <div className="foot"><Link to={`${PC_QUEUE_PATH}?loanId=${loanId}`}>Open the work queue</Link> to assign, acknowledge or snooze. <SmallButton type="button" onClick={() => void load()} disabled={busy} style={{ marginLeft: 6 }}><RefreshCw size={12} /> Refresh</SmallButton></div>
    </Rail>
    <Work aria-live="polite">
      {selected ? <>
        <div className="head">
          <div><StatusChip tone={severityTone(selected)} label={`${words(selected.severity)}${selected.blocking ? ' · blocking' : ''}`} size="md" /> <strong style={{ marginLeft: 6 }}>{selected.text}</strong></div>
          <Button $variant="primary" type="button" disabled={selected.actorMayAct === false}
            title={selected.actorMayAct === false ? `Requires ${selected.nextAction.requiredRoles.join(', ')}` : undefined}
            onClick={() => onOpen(selected.nextAction.workspace, selected.nextAction.query)}>{selected.nextAction.label} <ChevronRight size={14} /></Button>
        </div>
        <p className="meta">{words(selected.workflow)} · {selected.sla === 'UNASSIGNED' ? 'no due date' : `${words(selected.sla)}${selected.dueDate ? ` · due ${selected.dueDate}` : ''}`} · owner {selected.owner || 'none'}{selected.actorMayAct === false ? ` · needs ${selected.nextAction.requiredRoles.map(r => words(r)).join(', ')}` : ''}</p>
        <p className="meta"><code style={{ fontSize: 11, color: INK.faint }}>{selected.key}</code></p>
      </> : <>
        <h2>Nothing waits</h2>
        <p className="quiet" style={{ marginTop: 6 }}>Every workflow on this facility is clear. Servicing actions and the records are below.</p>
      </>}
    </Work>
  </Shell>;
};

export default FacilityAttentionRail;
